import { Pagination } from "antd";
import { apiRoot, apiV2Root } from "consts";
import CardSample from "features/cases/blocks/CommonCard";
import { isValidContentType } from "features/cases/contentType";
import {
  ContentType,
  EDITORIAL_UNREVIEWED_QUERY_KEY,
} from "features/editorial/types";
import ContentItemsView from "feeds/ContentItemsView";
import SortToggle from "feeds/SortToggle";
import type { SortDirection, SortField } from "feeds/SortToggle.types";
import { useDataQueryParams } from "hooks/useDataQueryParams";
import { useFlag } from "hooks/useFlags";
import { useSearchParam } from "hooks/useSearchParam";
import { useUser } from "hooks/useUser";
import React, { useState } from "react";
import type { Card, PaginatedResponse, Problem, Solution } from "types/basic";
import TabBar from "ui/TabBar";

import "./EditorialFeed.css";

type EditorialMode = "review" | "no_topic";

const modeOptions = [
  { value: "review" as EditorialMode, label: "Ревью" },
  { value: "no_topic" as EditorialMode, label: "Без темы" },
];

const typeOptions = [
  { value: "problem" as ContentType, label: "Проблемы" },
  { value: "solution" as ContentType, label: "Решения" },
  { value: "card" as ContentType, label: "Карточки" },
];

const EditorialFeed: React.FC = () => {
  const { isLoggedIn, isContentEditor } = useUser();
  const editorialEnabled = useFlag("editorialTest");

  const [rawMode, setRawMode] = useSearchParam("mode");
  const mode: EditorialMode = rawMode === "no_topic" ? "no_topic" : "review";

  const [rawType, setType] = useSearchParam("type");
  const type = isValidContentType(rawType) ? rawType : "problem";

  const [rawPage, setPage] = useSearchParam("page");
  const page = Number(rawPage) || 1;

  const [sortField, setSortField] = useState<SortField>("dateModified");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [groupByCase, setGroupByCase] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);

  const ordering = `${sortDirection === "desc" ? "-" : ""}${sortField}`;

  const onChangeMode = (newMode: EditorialMode) =>
    setRawMode(newMode, ["page", "type"]);

  const onChangeType = (newType: ContentType) => setType(newType, ["page"]);

  const onPageChange = (newPage: number) => setPage(String(newPage));

  const handleSortChange = (
    field: SortField,
    direction: SortDirection,
    newGroupByCase: boolean,
  ) => {
    setSortField(field);
    setSortDirection(direction);
    setGroupByCase(newGroupByCase);
  };

  const reviewResponse = useDataQueryParams<
    PaginatedResponse<Problem | Solution | Card>
  >(
    EDITORIAL_UNREVIEWED_QUERY_KEY,
    { type, page, ordering },
    apiV2Root,
    mode !== "review",
  );

  // Без темы — только проблемы: темы относятся к сфере, а сфера сегодня
  // проставляется только на проблеме, не на решении/карточке.
  const noTopicResponse = useDataQueryParams<PaginatedResponse<Problem>>(
    "cases",
    { hasTopic: 0, page, ordering },
    apiRoot,
    mode !== "no_topic",
  );

  if (!isLoggedIn) {
    return (
      <div>Пожалуйста, войдите, чтобы просматривать и ревьюить контент.</div>
    );
  }

  if (!isContentEditor) {
    return <div>У вас нет прав на редакторский доступ.</div>;
  }

  if (!editorialEnabled) {
    return <div>Редакторский функции выключены.</div>;
  }

  const casesResponse = mode === "review" ? reviewResponse : noTopicResponse;

  if (!casesResponse) return <div>Загрузка...</div>;

  return (
    <div className="editorial-feed">
      <TabBar
        value={mode}
        options={modeOptions}
        onChange={onChangeMode}
      />
      {mode === "review" && (
        <TabBar
          value={type}
          options={typeOptions}
          onChange={onChangeType}
        />
      )}
      <SortToggle
        onSortChange={handleSortChange}
        initialField={sortField}
        initialDirection={sortDirection}
        initialGroupByCase={groupByCase}
        initialShowList={showList}
        onDisplayModeChange={setShowList}
      />

      {mode === "review" ? (
        <ContentItemsView
          items={reviewResponse?.results || []}
          solutions={[]}
          showList={showList}
          sortField={sortField}
          isReviewing={true}
          fromEditorialFeed={true}
        />
      ) : (
        <div className="editorial-feed-no-topic-grid">
          {(noTopicResponse?.results || []).map((problem) => (
            <CardSample
              parentData={{
                isProblem: true,
                sphereId: problem?.sphere,
                isSolution: false,
                isAttribute: false,
                problemId: String(problem.id),
              }}
              cardContentId={String(problem.id)}
              sectionTitle={"Проблема"}
              mode={"wide"}
              key={"problem" + problem.id}
              meta={problem?.meta}
              title={problem?.title}
              description={problem?.description}
              url={`#/problems/${problem.id}/`}
              linkButtonText="Подробности"
            />
          ))}
        </div>
      )}

      {casesResponse.totalPages > 1 && (
        <div className="editorial-feed-pagination">
          <Pagination
            current={casesResponse.page}
            pageSize={casesResponse.pageSize}
            total={casesResponse.count}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default EditorialFeed;
